// Saved sign-in details (2026-10-29). Devices are shared on the floor, so
// unless an Admin has switched it on (Settings › Login screen; BE
// services/savedLogins.js), no Portal sign-in field may look like a
// credential to the browser: no type="password", no username /
// current-password / new-password autocomplete hints. A password manager
// then never offers to save one, and has nowhere to fill a credential a
// device saved before this shipped. The password is masked with CSS instead
// — see SecretInput.
//
// The answer comes from GET /login/methods (`savedLogins`). Until it arrives
// every screen behaves as "not allowed", and only a live answer opens it —
// never a value remembered on the device — so switching it off takes effect
// on the very next page load.

import { useEffect, useState } from "react";
import { apiFetch } from "../../api/apiFetch";
import type { LoginMethods } from "../../types/Auth";

// The last live answer in this tab, so account setup / change password —
// which follow the login page in-app — start from it without a round trip.
let lastKnown: boolean | null = null;

export const rememberSavedLogins = (allowed: boolean) => { lastKnown = allowed; };

/** Test seam: forget the in-tab answer. */
export const resetSavedLoginsMemory = () => { lastKnown = null; };

/** A /login/methods body allows saving only with a literal true. */
export const savedLoginsFrom = (body: Partial<LoginMethods> | null | undefined): boolean => body?.savedLogins === true;

/** Can this browser draw a text field as dots (-webkit-text-security)? Every current engine can. */
export const supportsMaskedText = (): boolean => {
    try {
        return typeof CSS !== "undefined" && typeof CSS.supports === "function"
            && CSS.supports("-webkit-text-security", "disc");
    } catch { return false; }
};

/** Opt-outs the common password-manager extensions honour (LastPass, 1Password, Bitwarden, Dashlane). */
export const PASSWORD_MANAGER_OPT_OUT = {
    "data-lpignore": "true",
    "data-1p-ignore": "true",
    "data-bwignore": "true",
    "data-form-type": "other",
} as const;

/** For the screens after login: false until GET /login/methods says otherwise. */
export const useSavedLoginsAllowed = (): boolean => {
    const [allowed, setAllowed] = useState<boolean>(lastKnown === true);
    useEffect(() => {
        if (lastKnown !== null) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await apiFetch(`/login/methods`, { method: "GET" });
                if (!res?.ok) return;
                const value = savedLoginsFrom(await res.json().catch(() => null));
                rememberSavedLogins(value);
                if (!cancelled) setAllowed(value);
            } catch { /* keep the default: not allowed */ }
        })();
        return () => { cancelled = true; };
    }, []);
    return allowed;
};
